import { useEffect } from 'react';
import { supported } from '@mapbox/mapbox-gl-supported';

import t from '../helpers/translate';
import { Sentry } from '../sentryUtils';
import { deeplinkUrl } from '../deeplinkUtils';
import ExternalLink from './ExternalLink';
import Prompt from './Prompt';
import Svg from './Svg';
import MapboxGlNotSupportedError from '../errors/MapboxGlNotSupportedError';
import { SyncReactNode } from '../types';

const IS_MAPBOX_GL_SUPPORTED = supported();

const helpLinks = [
  'https://www.mapbox.com/help/mapbox-browser-support/#mapbox-gl-js',
  'https://get.webgl.org',
];

const MapboxGlSupportBoundry = ({ children }: { children: SyncReactNode }) => {
  useEffect(() => {
    if (!IS_MAPBOX_GL_SUPPORTED) {
      const error = new MapboxGlNotSupportedError('Mapbox GL is not supported in this browser');
      Sentry.captureException(error);
    }
  }, []);

  if (IS_MAPBOX_GL_SUPPORTED) {
    return children;
  }

  return (
    <Prompt>
      <h1>{t('Uh-oh, something went wrong!')}</h1>
      <p>
        {t(
          'It seems that Mapbox GL, which is required to run the map widget, is not supported in your browser. It could be that WebGL is disabled in your browser.',
        )}
      </p>
      <h3>{t('Follow the links below for troubleshooting')}</h3>
      <ul>
        {helpLinks.map((helpLink) => (
          <li key={helpLink}>
            <ExternalLink href={helpLink}>{helpLink}</ExternalLink>
          </li>
        ))}
      </ul>
      <hr />
      <div className="cta-wrapper">
        <p>
          <ExternalLink href={deeplinkUrl('mapbox-gl-support-boundry')}>
            {t('Download the Fishbrain app')}
            <Svg className="cta-logo" icon="logo" />
          </ExternalLink>
        </p>
      </div>
      <p>
        <small>{t('Available for iOS and Android')}</small>
      </p>
    </Prompt>
  );
};

export default MapboxGlSupportBoundry;
